import { SortationCenterError, SortationGaylordError } from "@deliverr/sortation-client";
import { createSuccessNotification, log, logError, logStart } from "@deliverr/ui-facility";
import { useCommonFlow } from "@deliverr/ui-facility/lib-facility/flow/useCommonFlow";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useAsyncFn } from "react-use";
import { useRecoilValue } from "recoil";

import { sortationIdState } from "sortation/base/sortationIdState";
import { useSortationCenterError } from "sortation/base/useSortationCenterError";
import { useSortationClient } from "sortation/base/useSortationClient";
import { useSortationModal } from "sortation/components/SortationModal";
import { visibleSortationModalValue } from "sortation/components/SortationModal/SortationModalState";
import { TOAST_AUTO_CLOSE } from "sortation/utils/toastAutoClose";
import { StagingScanModal, stagingScanMessageMap } from "./modals";

export const useStagingScan = () => {
  const { formatMessage } = useIntl();
  const { successResponse, resetNotifications, addAutoCloseNotification, emitFlash } = useCommonFlow();
  const { handleSortationCenterError } = useSortationCenterError();
  const sortationId = useRecoilValue(sortationIdState);
  const sortationClient = useSortationClient();
  const { showModal } = useSortationModal();
  const visibleModal = useRecoilValue(visibleSortationModalValue);

  const [barcode, setBarcode] = useState<string>("");

  const handleError = useCallback(
    (err) => {
      const errCode = err.message;
      let modal: StagingScanModal;

      switch (errCode) {
        case SortationGaylordError.NOT_FOUND:
          modal = StagingScanModal.GAYLORD_NOT_FOUND;
          break;
        default:
          modal = StagingScanModal.UNKNOWN_ERROR;
          break;
      }

      emitFlash("DANGER");
      showModal(modal, stagingScanMessageMap[modal]);
    },
    [emitFlash, showModal]
  );

  const [handleStagingScanState, handleStagingScan] = useAsyncFn(
    async (input: string) => {
      if (visibleModal) {
        return;
      }

      const ctx = logStart({ fn: "useStagingScan.handleStagingScan", input, sortationId });

      setBarcode(input);
      resetNotifications();

      try {
        const response = await sortationClient.processStagingScan(input);

        successResponse();
        addAutoCloseNotification(
          createSuccessNotification(
            formatMessage(
              {
                id: "sortation.stagingScan.success",
                defaultMessage: "Gaylord {gaylordId} staged successfully",
              },
              {
                gaylordId: response.id,
              }
            )
          ),
          TOAST_AUTO_CLOSE
        );

        log(ctx, "Staging scan completed successfully", { gaylordId: response.id });
      } catch (err) {
        logError(ctx, err);
        if (err.message === SortationCenterError.SORTATION_CENTER_NOT_FOUND) {
          handleSortationCenterError();
          return;
        }
        handleError(err);
      } finally {
        setBarcode("");
      }
    },
    [
      sortationId,
      visibleModal,
      formatMessage,
      resetNotifications,
      sortationClient,
      successResponse,
      addAutoCloseNotification,
      handleSortationCenterError,
      handleError,
    ]
  );

  return {
    barcode,
    isLoading: handleStagingScanState.loading,
    setBarcode,
    handleStagingScan,
  };
};
